import { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { combineLatest } from 'zen-observable/extras';
import { ApolloQueryResult, OperationVariables, QueryOptions } from 'apollo-client';
import Observable from 'zen-observable';
import { DocumentNode } from 'graphql';
import { useDeepCompareMemoize } from 'use-deep-compare-effect';
import { captureException } from '@sentry/browser';
import { filterNullish } from '@modernhealth/filter-nullish';

/**
 * This hook returns the results of multiple queries after they _all_
 * have returned data.
 *
 * @param queries A list of query options _and_ a key to identify them. The
 * key can be any sort of data, including an object.
 * @returns the combined results
 */
export function useCombinedQueries<
    K,
    T extends unknown[] = unknown[],
    TVariables extends OperationVariables[] = OperationVariables[],
>(
    queries: { key: K; options: QueryOptions<TVariables[number]> }[],
): {
    loading: boolean;
    hasError: boolean;
    data:
        | {
              data: T[number];
              key: K;
          }[]
        | null;
} {
    type QueryT = T[number];
    type QueryTVariables = TVariables[number];

    const client = useApolloClient();
    const memoizedQueries = useDeepCompareMemoize(queries);

    const [queriesByKey, combinedObservable] = useMemo(() => {
        const observables = memoizedQueries.map(query => {
            // Watch the query for any updates...
            const observable = client.watchQuery<QueryT, QueryTVariables>(query.options);

            return {
                queryObservable: observable,
                key: query.key,
                // Due to https://github.com/apollographql/apollo-client/issues/5295
                // we can't use Apollo's observables directly when mapping...
                zenObservable: Observable.from(
                    observable as unknown as ZenObservable.ObservableLike<
                        ApolloQueryResult<QueryT | undefined | null>
                    >,
                    // Map the observable to always include the key,
                    // this enables users to identify the result
                ).map(result => ({
                    result,
                    key: query.key,
                })),
            };
        });

        // Combine all the observables into a single
        // observable which updates with the latest
        // value from any of its children
        const combinedObservable = combineLatest(
            ...observables.map(({ zenObservable }) => zenObservable),
        );

        return [
            observables.map(({ queryObservable, key }) => ({ query: queryObservable, key })),
            combinedObservable,
        ] as const;
    }, [client, memoizedQueries]);

    const [values, setValues] = useState<{
        loading: boolean;
        hasError: boolean;
        data: { data: QueryT; key: K }[] | null;
    }>(() => {
        const currentResults = queriesByKey.map(
            ({ key, query }) => [key, query.currentResult()] as const,
        );

        const dataUpdates = currentResults.map(([key, query]) => {
            return query.data
                ? {
                      data: query.data as QueryT,
                      key,
                  }
                : {
                      data: null,
                      key,
                  };
        });

        return {
            loading: currentResults.some(([, query]) => query.loading),
            hasError: currentResults.some(([, query]) => query.errors),
            data: dataUpdates.every(
                (result): result is { data: QueryT; key: K } => result.data != null,
            )
                ? dataUpdates
                : null,
        };
    });

    useEffect(() => {
        const subscription = combinedObservable.subscribe(queryUpdate => {
            const dataUpdates = queryUpdate.map(update => {
                return {
                    data: update.result.data,
                    key: update.key,
                };
            });

            const hasError = queryUpdate.some(update => update.result.errors);

            if (hasError) {
                // Report any errors to Sentry because we don't expose them
                // to users
                try {
                    filterNullish(queryUpdate.flatMap(update => update.result.errors)).forEach(
                        error => captureException(error),
                    );
                } catch (error) {
                    captureException(error);
                }
            }

            setValues({
                // We are still loading if _any_ of the queries is
                loading: queryUpdate.some(update => update.result.loading),
                // We have errors if _any_ of the queries does
                hasError,
                // Only return data if all are not nullish
                data: dataUpdates.every(
                    (result): result is { data: QueryT; key: K } => result.data != null,
                )
                    ? dataUpdates
                    : null,
            });
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [client, combinedObservable]);

    return values;
}

/**
 * This hook returns multiple calls of the same query after they _all_
 * have returned data.
 *
 * @param queries A list of query options _and_ a key to identify them. The
 * key can be any sort of data, including an object.
 * @returns the combined results
 *
 * @example
 * ```tsx
 *  useQueryWithMultipleCalls<
 *      TypeOfKeyObject,
 *      FetchInfoQuery,
 *      FetchInfoQueryVariables
 *  >(
 *      FetchInfoDocument,
 *      useMemo(
 *          () =>
 *              objects.map(object => ({
 *                  key: object,
 *                  variables: {
 *                      // ...
 *                  },
 *              })),
 *          [objects],
 *      ),
 *  );
 * };
 * ```
 */
export function useQueryWithMultipleCalls<
    K,
    T extends unknown,
    TVariables extends OperationVariables,
>(
    query: DocumentNode,
    calls: { key: K; variables: TVariables }[],
    options?: Omit<QueryOptions<TVariables>, 'variables'>,
): {
    loading: boolean;
    hasError: boolean;
    data:
        | {
              data: T;
              key: K;
          }[]
        | null;
} {
    const memoizedCalls = useDeepCompareMemoize(calls);
    const memoizedOptions = useDeepCompareMemoize(options);

    return useCombinedQueries<K, T[], TVariables[]>(
        useMemo(() => {
            return memoizedCalls.map(call => ({
                key: call.key,
                options: {
                    ...memoizedOptions,
                    variables: call.variables,
                    query,
                },
            }));
        }, [memoizedCalls, memoizedOptions, query]),
    );
}
